import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ajvResolver,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
  ValidatorsProps,
} from '@ui/src';
import {
  useEditPSPDetailsMutation,
  useLazyGetPSPDetailsQuery,
} from '../../../settings/store/services/details';
import { EditPSPOTPFormValues, PSPOTPKeys } from '../../../settings/types';
import { setIsDirty } from '../../../settings/store/slices/details';

const useOTP = () => {
  const {
    settings: {
      details: { validators, pspDetails },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [edit, { isLoading: editIsLoading }] = useEditPSPDetailsMutation();
  const [getDetails, { isLoading }] = useLazyGetPSPDetailsQuery();
  const [defaultFormValues, setDefaultFormValues] =
    useState<EditPSPOTPFormValues>();
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
    setValue,
  } = useForm<EditPSPOTPFormValues>({
    resolver: ajvResolver(validators?.updateSettingSchema as ValidatorsProps),
  });

  const fields = watch();

  const isDirty =
    !isEqual(fields, defaultFormValues) || Boolean(Object.keys(errors).length);

  const getDetailsAction = useCallback(async () => {
    const response = (await getDetails()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const updatedSettings: {
      [key: string]:
        | string
        | string[]
        | boolean
        | number
        | Record<string, string>
        | Record<string, boolean>;
      key: PSPOTPKeys;
    }[] = [];

    if (
      !isEqual(
        values.otpAuthAvailableMethods,
        defaultFormValues?.otpAuthAvailableMethods,
      )
    ) {
      updatedSettings.push({
        otpAuthAvailableMethods: values.otpAuthAvailableMethods as string[],
        key: PSPOTPKeys.OTP_CUSTOMERS_AUTH_AVAILABLE_METHODS,
      });
    }

    if (
      values.otpAuthDefaultMethod !== defaultFormValues?.otpAuthDefaultMethod
    ) {
      updatedSettings.push({
        otpAuthDefaultMethod: values.otpAuthDefaultMethod as string,
        key: PSPOTPKeys.OTP_CUSTOMERS_AUTH_DEFAULT_METHOD,
      });
    }

    if (
      !isEqual(
        values.otpTransactionSignAvailableMethods,
        defaultFormValues?.otpTransactionSignAvailableMethods,
      )
    ) {
      updatedSettings.push({
        otpTransactionSignAvailableMethods:
          values.otpTransactionSignAvailableMethods as string[],
        key: PSPOTPKeys.OTP_CUSTOMERS_TRANSACTION_SIGN_AVAILABLE_METHODS,
      });
    }

    if (
      values.otpTransactionSignDefaultMethod !==
      defaultFormValues?.otpTransactionSignDefaultMethod
    ) {
      updatedSettings.push({
        otpTransactionSignDefaultMethod:
          values.otpTransactionSignDefaultMethod as string,
        key: PSPOTPKeys.OTP_CUSTOMERS_TRANSACTION_SIGN_DEFAULT_METHOD,
      });
    }

    if (
      !isEqual(
        values.otpApplicationSignAvailableMethods,
        defaultFormValues?.otpApplicationSignAvailableMethods,
      )
    ) {
      updatedSettings.push({
        otpApplicationSignAvailableMethods:
          values.otpApplicationSignAvailableMethods as string[],
        key: PSPOTPKeys.OTP_CUSTOMERS_APPLICATION_SIGN_AVAILABLE_METHODS,
      });
    }

    if (
      values.otpApplicationSignDefaultMethod !==
      defaultFormValues?.otpApplicationSignDefaultMethod
    ) {
      updatedSettings.push({
        otpApplicationSignDefaultMethod:
          values.otpApplicationSignDefaultMethod as string,
        key: PSPOTPKeys.OTP_CUSTOMERS_APPLICATION_SIGN_DEFAULT_METHOD,
      });
    }

    if (
      !isEqual(
        values.otpAgreementSignAvailableMethods,
        defaultFormValues?.otpAgreementSignAvailableMethods,
      )
    ) {
      updatedSettings.push({
        otpAgreementSignAvailableMethods:
          values.otpAgreementSignAvailableMethods as string[],
        key: PSPOTPKeys.OTP_CUSTOMERS_AGREEMENT_SIGN_AVAILABLE_METHODS,
      });
    }

    if (
      values.otpAgreementSignDefaultMethod !==
      defaultFormValues?.otpAgreementSignDefaultMethod
    ) {
      updatedSettings.push({
        otpAgreementSignDefaultMethod:
          values.otpAgreementSignDefaultMethod as string,
        key: PSPOTPKeys.OTP_CUSTOMERS_AGREEMENT_SIGN_DEFAULT_METHOD,
      });
    }

    if (updatedSettings.length > 0) {
      const response = (await Promise.all(
        updatedSettings.map(async (item) => {
          const res = await edit(item);

          return res;
        }),
      )) as { error?: ResponseError }[];

      if (response.find((item) => item.error)) {
        const errorsResponse = response.filter((item) => item.error);

        errorsResponse.forEach((item) => {
          responseErrorsResolver({
            errors: item.error as ResponseError,
            setError,
            setGlobalError,
          });
        });
      } else {
        dispatch(setIsDirty(false));
      }

      setDefaultFormValues({
        otpAuthAvailableMethods: values?.otpAuthAvailableMethods || [],
        otpAuthDefaultMethod: values?.otpAuthDefaultMethod || '',
        otpTransactionSignAvailableMethods:
          values?.otpTransactionSignAvailableMethods || [],
        otpTransactionSignDefaultMethod:
          values?.otpTransactionSignDefaultMethod || '',
        otpApplicationSignAvailableMethods:
          values?.otpApplicationSignAvailableMethods || [],
        otpApplicationSignDefaultMethod:
          values?.otpApplicationSignDefaultMethod || '',
        otpAgreementSignAvailableMethods:
          values?.otpAgreementSignAvailableMethods || [],
        otpAgreementSignDefaultMethod:
          values?.otpAgreementSignDefaultMethod || '',
      });
    }
  });

  useEffect(() => {
    if (!pspDetails) {
      getDetailsAction();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (pspDetails) {
      const otpAuthAvailableMethods = pspDetails?.find(
        (item) => item.key === PSPOTPKeys.OTP_CUSTOMERS_AUTH_AVAILABLE_METHODS,
      );
      const otpAuthDefaultMethod = pspDetails?.find(
        (item) => item.key === PSPOTPKeys.OTP_CUSTOMERS_AUTH_DEFAULT_METHOD,
      );

      const otpTransactionSignAvailableMethods = pspDetails?.find(
        (item) =>
          item.key ===
          PSPOTPKeys.OTP_CUSTOMERS_TRANSACTION_SIGN_AVAILABLE_METHODS,
      );
      const otpTransactionSignDefaultMethod = pspDetails?.find(
        (item) =>
          item.key === PSPOTPKeys.OTP_CUSTOMERS_TRANSACTION_SIGN_DEFAULT_METHOD,
      );

      const otpApplicationSignAvailableMethods = pspDetails?.find(
        (item) =>
          item.key ===
          PSPOTPKeys.OTP_CUSTOMERS_APPLICATION_SIGN_AVAILABLE_METHODS,
      );
      const otpApplicationSignDefaultMethod = pspDetails?.find(
        (item) =>
          item.key === PSPOTPKeys.OTP_CUSTOMERS_APPLICATION_SIGN_DEFAULT_METHOD,
      );

      const otpAgreementSignAvailableMethods = pspDetails?.find(
        (item) =>
          item.key ===
          PSPOTPKeys.OTP_CUSTOMERS_AGREEMENT_SIGN_AVAILABLE_METHODS,
      );
      const otpAgreementSignDefaultMethod = pspDetails?.find(
        (item) =>
          item.key === PSPOTPKeys.OTP_CUSTOMERS_AGREEMENT_SIGN_DEFAULT_METHOD,
      );

      reset({
        otpAuthAvailableMethods:
          (otpAuthAvailableMethods?.data.value as string[]) || [],
        otpAuthDefaultMethod:
          (otpAuthDefaultMethod?.data.value as string) || '',
        otpTransactionSignAvailableMethods:
          (otpTransactionSignAvailableMethods?.data.value as string[]) || [],
        otpTransactionSignDefaultMethod:
          (otpTransactionSignDefaultMethod?.data.value as string) || '',
        otpApplicationSignAvailableMethods:
          (otpApplicationSignAvailableMethods?.data.value as string[]) || [],
        otpApplicationSignDefaultMethod:
          (otpApplicationSignDefaultMethod?.data.value as string) || '',
        otpAgreementSignAvailableMethods:
          (otpAgreementSignAvailableMethods?.data.value as string[]) || [],
        otpAgreementSignDefaultMethod:
          (otpAgreementSignDefaultMethod?.data.value as string) || '',
      });

      setDefaultFormValues({
        otpAuthAvailableMethods:
          (otpAuthAvailableMethods?.data.value as string[]) || [],
        otpAuthDefaultMethod:
          (otpAuthDefaultMethod?.data.value as string) || '',
        otpTransactionSignAvailableMethods:
          (otpTransactionSignAvailableMethods?.data.value as string[]) || [],
        otpTransactionSignDefaultMethod:
          (otpTransactionSignDefaultMethod?.data.value as string) || '',
        otpApplicationSignAvailableMethods:
          (otpApplicationSignAvailableMethods?.data.value as string[]) || [],
        otpApplicationSignDefaultMethod:
          (otpApplicationSignDefaultMethod?.data.value as string) || '',
        otpAgreementSignAvailableMethods:
          (otpAgreementSignAvailableMethods?.data.value as string[]) || [],
        otpAgreementSignDefaultMethod:
          (otpAgreementSignDefaultMethod?.data.value as string) || '',
      });
    }
  }, [pspDetails]);

  useEffect(() => {
    dispatch(setIsDirty(isDirty));
  }, [isDirty]);

  return {
    isLoading,
    errorList,
    control,
    onSubmit,
    globalError,
    editIsLoading,
    isDirty,
    fields,
    setValue,
  };
};

export default useOTP;
